import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useList } from "@/hooks/crud";
import { Spinner, ErrorBanner, Empty } from "@/components/Feedback";
import { StageBadge, RemixBadge } from "@/components/Badges";
import { TrackForm } from "./TrackForm";
import { PIPELINE_STAGES } from "@/lib/enums";
import { formatNumber } from "@/lib/format";
import type { Artist, Project, Track } from "@/types/database";

type TrackRow = Track & {
  artists?: { name: string } | null;
  projects?: { name: string } | null;
};

export default function TracksList() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("");
  const [artistId, setArtistId] = useState("");
  const [projectId, setProjectId] = useState("");

  const artists = useList("artists", "select=id,name&order=name");
  const projects = useList("projects", "select=id,name,artist_id&order=name");

  const query = useMemo(() => {
    const parts = [
      "select=*,artists(name),projects(name)",
      "order=updated_at.desc",
      "limit=500",
    ];
    if (stage) parts.push(`pipeline_stage=eq.${stage}`);
    if (artistId) parts.push(`artist_id=eq.${artistId}`);
    if (projectId) parts.push(`project_id=eq.${projectId}`);
    if (search.trim()) parts.push(`title=ilike.*${encodeURIComponent(search.trim())}*`);
    return parts.join("&");
  }, [stage, artistId, projectId, search]);

  const { data, isLoading, error, isFetching } = useList("tracks", query);
  const rows = (data?.rows ?? []) as TrackRow[];
  const artistList = (artists.data?.rows ?? []) as Artist[];
  const projectList = ((projects.data?.rows ?? []) as Project[]).filter(
    (p) => !artistId || p.artist_id === Number(artistId),
  );

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Tracks</h1>
          <div className="page-sub">
            {data ? `${formatNumber(data.total)} matching` : "—"}
            {isFetching ? " · refreshing…" : ""}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          + New Track
        </button>
      </div>

      <div className="toolbar">
        <input
          className="search"
          placeholder="Search title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={stage} onChange={(e) => setStage(e.target.value)}>
          <option value="">All stages</option>
          {PIPELINE_STAGES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={artistId}
          onChange={(e) => {
            setArtistId(e.target.value);
            setProjectId("");
          }}
        >
          <option value="">All artists</option>
          {artistList.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">All projects</option>
          {projectList.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>No tracks match these filters.</Empty>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Artist</th>
                <th>Project</th>
                <th>Stage</th>
                <th className="num">BPM</th>
                <th>Key</th>
                <th className="num">Plays</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} onClick={() => navigate(`/tracks/${t.id}`)}>
                  <td>
                    {t.title} {t.is_remix && <RemixBadge />}
                  </td>
                  <td className="muted">{t.artists?.name ?? "—"}</td>
                  <td className="muted">{t.projects?.name ?? "—"}</td>
                  <td>
                    <StageBadge stage={t.pipeline_stage} />
                  </td>
                  <td className="num">{t.bpm ?? "—"}</td>
                  <td className="muted">{t.key_signature ?? "—"}</td>
                  <td className="num">{formatNumber(t.sc_playback_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <TrackForm
          onClose={() => setCreating(false)}
          onSaved={(t) => navigate(`/tracks/${t.id}`)}
        />
      )}
    </div>
  );
}
